/**
 * Embedded context window & compaction settings panel.
 * Rendered as the "Context Window" tab on the Agent detail page.
 *
 * WHY: Uses @coordio/ui FormField + FormSection + design tokens instead of
 * raw Tailwind labels and MUI Slider for consistent Cortex foundation UI.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button, CoordioDropdown, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { contextWindowConfigService } from '../../services/contextWindowConfigService';
import { agentService } from '../../services/agentService';
import {
  ContextSummarizationStrategy,
  SummarizerType,
  type UpdateContextWindowConfigRequest,
} from '../../types/contextWindow';
import type {
  AgentDefinitionDto,
  AgentImageContextMode,
  UpdateAgentParameterRequest,
  UpdateAgentRequest,
} from '../../types/agent';
import { useNotificationStore } from '../../store/notificationStore';

interface AgentContextWindowPanelProps {
  agent: AgentDefinitionDto;
  readOnly?: boolean;
}

const DEFAULT_FORM: UpdateContextWindowConfigRequest = {
  maxContextTokens: 32000,
  summarizationThreshold: 28000,
  summarizationStrategy: ContextSummarizationStrategy.LastXMinusY,
  summarizerType: SummarizerType.Standard,
  customSummarizerPrompt: null,
  messagesToRetain: 10,
  messagesToSummarize: 50,
  enableAutoSummarization: true,
  compressionRatio: 0.3,
  priorityMessagesJson: null,
};

export const AgentContextWindowPanel = ({ agent, readOnly = false }: AgentContextWindowPanelProps) => {
  const { addNotification } = useNotificationStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * WHY: API may return ImageContextMode as PascalCase string ("OcrToContext") or numeric enum (0).
   * Normalize to the camelCase keys the dropdown options expect.
   */
  const [imageContextMode, setImageContextMode] = useState<AgentImageContextMode>(() => {
    const raw = agent.imageContextMode;
    if (typeof raw === 'number') {
      return (['ocrToContext', 'descriptionToContext', 'ignoreImage', 'visionOnlyNoCompaction'] as const)[raw] ?? 'ocrToContext';
    }
    if (typeof raw === 'string' && raw.length > 0) {
      const camel = raw.charAt(0).toLowerCase() + raw.slice(1);
      if (['ocrToContext', 'descriptionToContext', 'ignoreImage', 'visionOnlyNoCompaction'].includes(camel)) {
        return camel as AgentImageContextMode;
      }
    }
    return 'ocrToContext';
  });
  const [form, setForm] = useState<UpdateContextWindowConfigRequest>(DEFAULT_FORM);

  const canSave = useMemo(() => {
    if (readOnly) return false;
    if (form.maxContextTokens < 1024) return false;
    if (form.summarizationThreshold < 1 || form.summarizationThreshold > form.maxContextTokens) return false;
    if (form.compressionRatio <= 0 || form.compressionRatio >= 1) return false;
    if (form.summarizerType === SummarizerType.Custom && !form.customSummarizerPrompt?.trim()) return false;
    return true;
  }, [readOnly, form]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = await contextWindowConfigService.getConfig(agent.id);
        if (!isMounted) return;
        setForm({
          maxContextTokens: cfg.maxContextTokens,
          summarizationThreshold: cfg.summarizationThreshold,
          summarizationStrategy: cfg.summarizationStrategy,
          summarizerType: cfg.summarizerType,
          customSummarizerPrompt: cfg.customSummarizerPrompt ?? null,
          messagesToRetain: cfg.messagesToRetain,
          messagesToSummarize: cfg.messagesToSummarize,
          enableAutoSummarization: cfg.enableAutoSummarization,
          compressionRatio: cfg.compressionRatio,
          priorityMessagesJson: cfg.priorityMessagesJson ?? null,
        });
        {
          const raw = agent.imageContextMode;
          const norm =
            typeof raw === 'number'
              ? (['ocrToContext', 'descriptionToContext', 'ignoreImage', 'visionOnlyNoCompaction'] as const)[raw] ?? 'ocrToContext'
              : typeof raw === 'string' && raw.length > 0
                ? (raw.charAt(0).toLowerCase() + raw.slice(1)) as AgentImageContextMode
                : 'ocrToContext';
          setImageContextMode(norm);
        }
      } catch (err: any) {
        if (!isMounted) return;
        setError(
          err?.response?.data?.message || err?.message || 'Failed to load context window configuration',
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [agent.id, agent.imageContextMode]);

  const handleSave = async () => {
    if (!canSave) {
      addNotification('Please fix validation errors before saving', 'warning');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await contextWindowConfigService.updateConfig(agent.id, form);
      const updatePayload: UpdateAgentRequest = {
        name: agent.name,
        slug: agent.slug,
        description: agent.description,
        systemPrompt: agent.systemPrompt,
        category: agent.category,
        iconName: agent.iconName,
        sortOrder: agent.sortOrder,
        includeAllDefaultTools: agent.includeAllDefaultTools,
        fanOut: agent.fanOut,
        useOpenAiHostedSkills: agent.useOpenAiHostedSkills,
        useHarnessAgent: agent.useHarnessAgent,
        injectSkillInstructionsAsUserContext: agent.injectSkillInstructionsAsUserContext,
        imageContextMode,
        isActive: agent.isActive,
        contextLimitLastNMessages: agent.contextLimitLastNMessages ?? null,
        contextLimitLastNDays: agent.contextLimitLastNDays ?? null,
        defaultModelId: agent.defaultModelId,
        parameters: agent.parameters.map((p) => ({
          id: p.id,
          key: p.key,
          label: p.label,
          description: p.description,
          type: p.type,
          defaultValue: p.defaultValue,
          isRequired: p.isRequired,
          isSecret: p.isSecret,
          validationJson: p.validationJson,
        })) as UpdateAgentParameterRequest[],
      };
      await agentService.updateAgent(agent.id, updatePayload);
      addNotification('Context window configuration saved', 'success');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to save configuration';
      setError(message);
      addNotification(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FormSection title="Context window & compaction">
        <LoadingSkeleton rows={6} />
      </FormSection>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {error && (
        <InlineErrorPanel
          message={error}
          action={
            <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          }
        />
      )}

      {readOnly && (
        <InlineErrorPanel
          title="System agent"
          message="System agents cannot be modified. Clone this agent to customize its context window settings."
        />
      )}

      {/* ─── Token limits & strategy ──────────────────────────────────────── */}
      <FormSection
        title="Token limits & strategy"
        description="Max tokens, summarization threshold, and the algorithm used to compact history."
        data-testid="agent-ctx-section-limits"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Max context tokens"
            error={form.maxContextTokens < 1024 ? 'Must be at least 1 024' : undefined}
            required
          >
            <Input
              type="number"
              value={form.maxContextTokens}
              onChange={(e) => setForm((s) => ({ ...s, maxContextTokens: Number(e.target.value) }))}
              disabled={readOnly}
            />
          </FormField>

          <FormField
            label="Summarization threshold"
            error={
              form.summarizationThreshold < 1 || form.summarizationThreshold > form.maxContextTokens
                ? 'Must be between 1 and Max context tokens'
                : undefined
            }
            required
          >
            <Input
              type="number"
              value={form.summarizationThreshold}
              onChange={(e) => setForm((s) => ({ ...s, summarizationThreshold: Number(e.target.value) }))}
              disabled={readOnly}
            />
          </FormField>

          <FormField label="Strategy">
            <CoordioDropdown
              options={[
                { value: String(ContextSummarizationStrategy.LastXMinusY), label: 'Last X minus Y' },
                { value: String(ContextSummarizationStrategy.SlidingWindow), label: 'Sliding window' },
                { value: String(ContextSummarizationStrategy.ImportanceBased), label: 'Importance based' },
                { value: String(ContextSummarizationStrategy.TimeBased), label: 'Time based' },
                { value: String(ContextSummarizationStrategy.Hybrid), label: 'Hybrid' },
              ]}
              value={String(form.summarizationStrategy)}
              onChange={(val) => setForm((s) => ({ ...s, summarizationStrategy: Number(val) as any }))}
              placeholder="Select strategy"
              size="sm"
              clearable={false}
              disabled={readOnly}
            />
          </FormField>

          <FormField label="Summarizer">
            <CoordioDropdown
              options={[
                { value: String(SummarizerType.Standard), label: 'Standard' },
                { value: String(SummarizerType.Custom), label: 'Custom prompt' },
              ]}
              value={String(form.summarizerType)}
              onChange={(val) => setForm((s) => ({ ...s, summarizerType: Number(val) as any }))}
              placeholder="Select summarizer"
              size="sm"
              clearable={false}
              disabled={readOnly}
            />
          </FormField>

          <FormField label="Messages to retain">
            <Input
              type="number"
              value={form.messagesToRetain}
              onChange={(e) => setForm((s) => ({ ...s, messagesToRetain: Number(e.target.value) }))}
              disabled={readOnly}
            />
          </FormField>

          <FormField label="Messages to summarize">
            <Input
              type="number"
              value={form.messagesToSummarize}
              onChange={(e) => setForm((s) => ({ ...s, messagesToSummarize: Number(e.target.value) }))}
              disabled={readOnly}
            />
          </FormField>
        </div>
      </FormSection>

      {/* ─── Auto compaction ──────────────────────────────────────────────── */}
      <FormSection
        title="Auto compaction"
        description="When on, chat history is automatically summarized when the context exceeds the threshold, so long sessions stay within limits."
        data-testid="agent-ctx-section-compaction"
      >
        <div className="flex items-center gap-3">
          <Switch
            checked={form.enableAutoSummarization}
            onCheckedChange={(checked) => setForm((s) => ({ ...s, enableAutoSummarization: checked }))}
            disabled={readOnly}
          />
          <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
            {form.enableAutoSummarization ? 'Auto compaction on' : 'Auto compaction off'}
          </span>
        </div>
      </FormSection>

      {/* ─── Image context ────────────────────────────────────────────────── */}
      <FormSection
        title="Image context"
        description="Controls how image attachments are turned into text for the model (layout/OCR, AI description, omitted, or native vision)."
        data-testid="agent-ctx-section-image"
      >
        <div className="max-w-md">
          <FormField label="Image handling">
            <CoordioDropdown
              options={[
                { value: 'ocrToContext', label: 'OCR / layout text' },
                { value: 'descriptionToContext', label: 'AI image description' },
                { value: 'ignoreImage', label: 'Ignore attachments' },
                { value: 'visionOnlyNoCompaction', label: 'Vision only (no preprocessing)' },
              ]}
              value={imageContextMode}
              onChange={(val) => setImageContextMode((val ?? 'ocrToContext') as AgentImageContextMode)}
              placeholder="Select image handling"
              size="sm"
              clearable={false}
              disabled={readOnly}
            />
          </FormField>
        </div>
      </FormSection>

      {/* ─── Compression ──────────────────────────────────────────────────── */}
      <FormSection
        title="Compression"
        description="Target ratio for how much content is kept after summarization."
        data-testid="agent-ctx-section-compression"
      >
        <FormField label={`Compression ratio — ${(form.compressionRatio * 100).toFixed(0)}% of original`}>
          <input
            type="range"
            min={0.05}
            max={0.95}
            step={0.05}
            value={form.compressionRatio}
            onChange={(e) => setForm((s) => ({ ...s, compressionRatio: Number(e.target.value) }))}
            disabled={readOnly}
            className="w-full accent-[var(--color-accent-primary)]"
            style={{ height: 6 }}
          />
        </FormField>
      </FormSection>

      {/* ─── Custom summarizer prompt (conditional) ───────────────────────── */}
      {form.summarizerType === SummarizerType.Custom && (
        <FormSection
          title="Custom summarizer prompt"
          data-testid="agent-ctx-section-custom-prompt"
        >
          <FormField
            label="Prompt"
            error={!form.customSummarizerPrompt?.trim() ? 'Required when using Custom prompt' : undefined}
            required
          >
            <Textarea
              value={form.customSummarizerPrompt ?? ''}
              onChange={(e) => setForm((s) => ({ ...s, customSummarizerPrompt: e.target.value }))}
              disabled={readOnly}
              rows={6}
            />
          </FormField>
        </FormSection>
      )}

      {/* ─── Priority messages ────────────────────────────────────────────── */}
      <FormSection
        title="Priority messages"
        description="Optional JSON array of message IDs that should never be dropped during summarization."
        data-testid="agent-ctx-section-priority"
      >
        <FormField label="Priority messages JSON">
          <Textarea
            value={form.priorityMessagesJson ?? ''}
            onChange={(e) => setForm((s) => ({ ...s, priorityMessagesJson: e.target.value }))}
            disabled={readOnly}
            rows={3}
          />
        </FormField>
      </FormSection>

      {/* ─── Save ─────────────────────────────────────────────────────────── */}
      {!readOnly && (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            variant="default"
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving ? 'Saving…' : 'Save configuration'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AgentContextWindowPanel;
